import { Injectable, NgZone, inject, PLATFORM_ID, OnDestroy, effect, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { fromEvent, merge, Subscription, timer, Subject } from 'rxjs';
import { startWith, switchMap, throttleTime, tap, filter } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService implements OnDestroy {
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private authService = inject(AuthService);

  private timeoutSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private readonly crossTabActivity$ = new Subject<void>();
  private initTimeoutId?: any;

  // 1 minute in milliseconds
  private readonly INACTIVITY_TIMEOUT = 3600000;

  constructor() {
    effect(() => {
      const user = this.authService.user();
      if (user) {
        this.ngZone.run(() => {
          this.startMonitoring();
        });
      } else {
        untracked(() => {
          this.stopMonitoring();
        });
      }
    });
  }

  public getSessionId(): string | null {
    return this.sessionId;
  }

  public getSession(): string | null {
    return this.sessionId;
  }

  private get sessionId(): string | null {
    return this.authService.getSessionId();
  }

  private get lastActivityKey(): string | null {
    const id = this.sessionId;
    return id ? `scout_last_activity_${id}` : null;
  }

  private get logoutEventKey(): string | null {
    const id = this.sessionId;
    return id ? `scout_logout_event_${id}` : null;
  }

  private get logoutTypeKey(): string | null {
    const id = this.sessionId;
    return id ? `scout_logout_type_${id}` : null;
  }

  private getRedirectUrl(page: 'login' | 'sessiontimeout'): string {
    let baseHref = '/';
    if (typeof document !== 'undefined') {
      const baseEl = document.querySelector('base');
      if (baseEl) {
        baseHref = baseEl.getAttribute('href') || '/';
      }
    }
    if (!baseHref.endsWith('/')) {
      baseHref += '/';
    }
    if (page === 'login') {
      return baseHref + '#/login';
    } else {
      return baseHref + 'sessiontimeout';
    }
  }

  public startMonitoring(): void {
    // Do not run on the server during SSR where `document` is undefined
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Stop any existing monitoring to avoid duplicate listeners and timers
    this.stopMonitoring();

    // Immediately set the last activity timestamp if the user is on a valid route
    const url = this.router.url;
    if (!url.includes('/login') && !url.includes('/sessiontimeout')) {
      this.setInitialActivity();
    }

    // Set activity when the user successfully navigates to a protected route (e.g., right after login)
    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (!event.urlAfterRedirects.includes('/login') && !event.urlAfterRedirects.includes('/sessiontimeout')) {
        this.setInitialActivity();
      }
    });

    // Listen for storage events from other tabs
    window.addEventListener('storage', this.handleStorageEvent);

    // Run outside Angular to avoid triggering change detection on every mousemove
    this.ngZone.runOutsideAngular(() => {
      // Defer the monitoring setup so it doesn't block the initial page render/hydration
      this.initTimeoutId = setTimeout(() => {
        const currentUrl = this.router.url;
        if (!currentUrl.includes('/login') && !currentUrl.includes('/sessiontimeout')) {
          this.setInitialActivity();
        }

        const eventOpts = { passive: true, capture: true }; // prevents scroll/mouse jank on the UI thread, capture ensures scroll works on all elements
        const domEvents$ = merge(
          /*   fromEvent(document, 'mousemove', eventOpts), */
          fromEvent(document, 'keydown', eventOpts),
          fromEvent(document, 'click', eventOpts),
          fromEvent(document, 'scroll', eventOpts),
          fromEvent(document, 'wheel', eventOpts),
          fromEvent(document, 'touchstart', eventOpts)
        ).pipe(
          throttleTime(0), // Increased to 10s to drastically reduce cross-tab sync broadcasts
          tap(() => this.setLastActivity())
        );

        const allActivity$ = merge(domEvents$, this.crossTabActivity$);

        this.timeoutSubscription = allActivity$.pipe(
          startWith(0), // Start the timer immediately on subscription
          switchMap(() => {
            const key = this.lastActivityKey;
            let timeRemaining = this.INACTIVITY_TIMEOUT;

            if (key) {
              const lastActivityStr = localStorage.getItem(key);
              if (lastActivityStr) {
                const lastActivity = parseInt(lastActivityStr, 10);
                if (lastActivity > 0) {
                  const elapsed = Date.now() - lastActivity;
                  timeRemaining = Math.max(0, this.INACTIVITY_TIMEOUT - elapsed);
                }
              }
            }
            return timer(timeRemaining);
          })
        ).subscribe(() => {
          this.ngZone.run(() => {
            this.handleInactivity();
          });
        });
      }, 5000); // Wait 5 seconds to guarantee Angular completely finishes rendering all tabs
    });
  }

  public stopMonitoring(): void {
    if (this.initTimeoutId) {
      clearTimeout(this.initTimeoutId);
      this.initTimeoutId = undefined;
    }
    if (this.timeoutSubscription) {
      this.timeoutSubscription.unsubscribe();
      this.timeoutSubscription = undefined;
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = undefined;
    }
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('storage', this.handleStorageEvent);
    }
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  private handleStorageEvent = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage || !event.key) return;

    // Fast-fail to prevent evaluating `this.sessionId` (which could be expensive) on unrelated storage events
    if (!event.key.startsWith('scout_')) return;

    // 1. Cross-tab activity
    const activityKey = this.lastActivityKey;
    if (activityKey && event.key === activityKey && event.newValue) {
      const url = this.router.url;
      if (!url.includes('/login') && !url.includes('/sessiontimeout')) {
        this.crossTabActivity$.next();
      }
    }

    // 2. Cross-tab logout
    const logoutKey = this.logoutEventKey;
    if (logoutKey && event.key === logoutKey && event.newValue) {
      const typeKey = this.logoutTypeKey;
      const logoutType = typeKey ? localStorage.getItem(typeKey) : 'manual';
      this.ngZone.run(() => {
        this.logoutFromOtherTab(logoutType || 'manual');
      });
    }
  };

  private setInitialActivity(): void {
    const key = this.lastActivityKey;
    if (!key) return;
    try {
      if (!localStorage.getItem(key)) {
        this.setLastActivity();
      }
    } catch {
      // ignore
    }
  }

  private setLastActivity(): void {
    const key = this.lastActivityKey;
    if (!key) return;

    try {
      localStorage.setItem(key, Date.now().toString());
    } catch {
      // ignore storage errors in restricted environments
    }
  }

  private notifyLogout(type: 'auto' | 'manual'): void {
    const logoutKey = this.logoutEventKey;
    const typeKey = this.logoutTypeKey;

    try {
      if (typeKey) localStorage.setItem(typeKey, type);
      if (logoutKey) localStorage.setItem(logoutKey, Date.now().toString());
    } catch { }
  }

  private handleInactivity(): void {

    const userSession = sessionStorage.getItem('user.session');

    if (userSession) {
      localStorage.removeItem(`scout_last_activity_${userSession}`);
    }

    const cleanup = () => {
      if (userSession) {
        localStorage.removeItem(`scout_logout_event_${userSession}`);
        localStorage.removeItem(`scout_logout_type_${userSession}`);
      }
    };

    const url = this.router.url;
    if (url.includes('/login') || url.includes('/sessiontimeout')) {
      return;
    }

    setTimeout(cleanup, 1500);
    window.addEventListener('pagehide', cleanup, { once: true });

    this.stopMonitoring();
    this.notifyLogout('auto');
    this.authService.logout();

    try {
      sessionStorage.clear();
      (this.authService as any)._currentUser?.set(null);
    } catch { }

    window.location.href = this.getRedirectUrl('sessiontimeout');

  }

  public manualLogout(): void {

    const sessionId = this.sessionId;

    localStorage.removeItem(`scout_last_activity_${sessionId}`);

    // Clean up the remaining keys after a short delay so other tabs have time to process the logout
    const cleanup = () => {
      localStorage.removeItem(`scout_logout_event_${sessionId}`);
      localStorage.removeItem(`scout_logout_type_${sessionId}`);
    };

    setTimeout(cleanup, 1500);
    window.addEventListener('pagehide', cleanup, { once: true });

    this.stopMonitoring();
    this.notifyLogout('manual');
    this.authService.logout();
  }

  private logoutFromOtherTab(logoutType: string): void {
    const url = this.router.url;
    const targetPage = logoutType === 'auto' ? 'sessiontimeout' : 'login';

    if (!url.includes('/' + targetPage) && !url.includes('/sessiontimeout')) {
      this.stopMonitoring();

      try {
        sessionStorage.clear();
        if ((this.authService as any)._currentUser) {
          (this.authService as any)._currentUser.set(null);
        }
      } catch { /* ignore */ }

      // FORCE THE BROWSER TO REDIRECT: 
      // This destroys all active modals, panels, and routing guards instantly.
      window.location.href = this.getRedirectUrl(logoutType === 'auto' ? 'sessiontimeout' : 'login');
    }
  }
}